import { TouchEvent, useEffect, useRef, useState } from 'react';

import {
  TOOLTIP_ENTER_DELAY,
  TOOLTIP_ENTER_TOUCH_DELAY,
  TOOLTIP_LEAVE_DELAY,
  TOOLTIP_LEAVE_TOUCH_DELAY,
  TOOLTIP_REENTRY_WINDOW,
  TOOLTIP_TOUCH_RESET_DELAY,
} from '../constants/tooltipTiming';
import { isFocusVisible } from '../functions/isFocusVisible';
import { UseTooltipTriggersParams } from '../types/UseTooltipTriggersParams';
import { UseTooltipTriggersResult } from '../types/UseTooltipTriggersResult';

export const useTooltipTriggers = ({
  popperRef,
}: UseTooltipTriggersParams): UseTooltipTriggersResult => {
  const [open, setOpen] = useState(false);
  const [touch, setTouch] = useState(false);
  const enterTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const leaveTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const touchTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const touchResetTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const ignoreMouse = useRef(false);
  const shown = useRef(false);
  const lastClosedAt = useRef(-Infinity);

  const show = (): void => {
    shown.current = true;
    setOpen(true);
  };

  const close = (): void => {
    // Only a tooltip that was showing lets the next one skip the enter delay
    if (shown.current) {
      lastClosedAt.current = Date.now();
    }

    shown.current = false;
    setOpen(false);

    clearTimeout(touchResetTimer.current);
    touchResetTimer.current = setTimeout((): void => {
      ignoreMouse.current = false;
      setTouch(false);
    }, TOOLTIP_TOUCH_RESET_DELAY);
  };

  const enter = (fromTouch: boolean): void => {
    // A tap is followed by emulated mouse events, which must not take over
    if (ignoreMouse.current && !fromTouch) {
      return;
    }

    clearTimeout(enterTimer.current);
    clearTimeout(leaveTimer.current);

    const reentry = Date.now() - lastClosedAt.current < TOOLTIP_REENTRY_WINDOW;

    enterTimer.current = setTimeout(show, reentry ? 0 : TOOLTIP_ENTER_DELAY);
  };

  const leave = (): void => {
    clearTimeout(enterTimer.current);
    clearTimeout(leaveTimer.current);
    leaveTimer.current = setTimeout(close, TOOLTIP_LEAVE_DELAY);
  };

  const isOnTooltip = (event: TouchEvent<HTMLElement>): boolean =>
    Boolean(popperRef.current?.contains(event.target as Node));

  useEffect((): (() => void) | undefined => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        clearTimeout(leaveTimer.current);
        close();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return (): void => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(
    (): (() => void) => (): void => {
      [enterTimer, leaveTimer, touchTimer, touchResetTimer].forEach(
        (timer): void => {
          clearTimeout(timer.current);
        },
      );
    },
    [],
  );

  return {
    handleBlur: (event): void => {
      if (!isFocusVisible(event.target)) {
        leave();
      }
    },
    handleFocus: (event): void => {
      if (isFocusVisible(event.target)) {
        enter(false);
      }
    },
    handleMouseLeave: (): void => {
      leave();
    },
    handleMouseOver: (): void => {
      enter(false);
    },
    handleTouchEnd: (event): void => {
      if (isOnTooltip(event)) {
        return;
      }

      // The enter delay keeps running: a tap shorter than it still opens the tooltip
      clearTimeout(touchTimer.current);
      clearTimeout(leaveTimer.current);
      leaveTimer.current = setTimeout(close, TOOLTIP_LEAVE_TOUCH_DELAY);
    },
    handleTouchStart: (event): void => {
      if (isOnTooltip(event)) {
        return;
      }

      ignoreMouse.current = true;
      setTouch(true);
      clearTimeout(leaveTimer.current);
      clearTimeout(touchResetTimer.current);
      clearTimeout(touchTimer.current);
      touchTimer.current = setTimeout((): void => {
        enter(true);
      }, TOOLTIP_ENTER_TOUCH_DELAY);
    },
    open,
    touch,
  };
};
